import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/wix-ecommerce-shipments-local-delivery-web',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/stores/v1/localDeliveryOptions',
        destPath: '/v1/localDeliveryOptions',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/local-delivery-server',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/local-delivery-server',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/wix-ecommerce-shipments-local-delivery-web',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/wix-ecommerce-shipments-local-delivery-web',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_local-delivery-options';

export function createLocalDeliveryOption(
  payload: object
): RequestOptionsFactory<any> {
  function __createLocalDeliveryOption({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'localDeliveryOption.minimumFreeDeliveryOrderAmount' },
          { path: 'localDeliveryOption.radiusConfig.address.geocode.latitude' },
          {
            path: 'localDeliveryOption.radiusConfig.address.geocode.longitude',
          },
          { path: 'localDeliveryOption.customAreaConfig.geocodes.latitude' },
          { path: 'localDeliveryOption.customAreaConfig.geocodes.longitude' },
          { path: 'localDeliveryOption.radiusConfig.radius' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'localDeliveryOption.createdDate' },
          { path: 'localDeliveryOption.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.local_delivery_option',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.shipments.localdelivery.LocalDelivery.CreateLocalDeliveryOption',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl({
        protoPath: '/v1/local-delivery-options',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'localDeliveryOption.minimumFreeDeliveryOrderAmount' },
              {
                path: 'localDeliveryOption.radiusConfig.address.geocode.latitude',
              },
              {
                path: 'localDeliveryOption.radiusConfig.address.geocode.longitude',
              },
              {
                path: 'localDeliveryOption.customAreaConfig.geocodes.latitude',
              },
              {
                path: 'localDeliveryOption.customAreaConfig.geocodes.longitude',
              },
              { path: 'localDeliveryOption.radiusConfig.radius' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'localDeliveryOption.createdDate' },
              { path: 'localDeliveryOption.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createLocalDeliveryOption;
}

export function getLocalDeliveryOption(
  payload: object
): RequestOptionsFactory<any> {
  function __getLocalDeliveryOption({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.local_delivery_option',
      method: 'GET' as any,
      methodFqn:
        'com.wix.ecom.shipments.localdelivery.LocalDelivery.GetLocalDeliveryOption',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl({
        protoPath: '/v1/local-delivery-options/{localDeliveryOptionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'localDeliveryOption.minimumFreeDeliveryOrderAmount' },
              {
                path: 'localDeliveryOption.radiusConfig.address.geocode.latitude',
              },
              {
                path: 'localDeliveryOption.radiusConfig.address.geocode.longitude',
              },
              {
                path: 'localDeliveryOption.customAreaConfig.geocodes.latitude',
              },
              {
                path: 'localDeliveryOption.customAreaConfig.geocodes.longitude',
              },
              { path: 'localDeliveryOption.radiusConfig.radius' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'localDeliveryOption.createdDate' },
              { path: 'localDeliveryOption.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'GET' as any,
          url: resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl({
            protoPath: '/v1/local-delivery-options/{localDeliveryOptionId}',
            data: payload,
            host,
          }),
          params: toURLSearchParams(payload),
        },
      ],
    };

    return metadata;
  }

  return __getLocalDeliveryOption;
}

/** @deprecated method is deprecated due to the fact that external_id is deprecated, it has been replaced with ListLocalDeliveryOptionsByDeliveryRegion(), and will be removed on 2024-11-01. */
export function listLocalDeliveryOptions(
  payload: object
): RequestOptionsFactory<any> {
  function __listLocalDeliveryOptions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.local_delivery_option',
      method: 'GET' as any,
      methodFqn:
        'com.wix.ecom.shipments.localdelivery.LocalDelivery.ListLocalDeliveryOptions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl({
        protoPath: '/v1/localDeliveryOptions',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'localDeliveryOptions.minimumFreeDeliveryOrderAmount' },
              {
                path: 'localDeliveryOptions.radiusConfig.address.geocode.latitude',
              },
              {
                path: 'localDeliveryOptions.radiusConfig.address.geocode.longitude',
              },
              {
                path: 'localDeliveryOptions.customAreaConfig.geocodes.latitude',
              },
              {
                path: 'localDeliveryOptions.customAreaConfig.geocodes.longitude',
              },
              { path: 'localDeliveryOptions.radiusConfig.radius' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'localDeliveryOptions.createdDate' },
              { path: 'localDeliveryOptions.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listLocalDeliveryOptions;
}

/**
 * Retrieves a list of local delivery options, given the provided paging, filtering, and sorting.
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
 * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/sorting-and-paging),
 * and [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
 */
export function queryLocalDeliveryOptions(
  payload: object
): RequestOptionsFactory<any> {
  function __queryLocalDeliveryOptions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.local_delivery_option',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.shipments.localdelivery.LocalDelivery.QueryLocalDeliveryOptions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl({
        protoPath: '/v1/local-delivery-options/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'localDeliveryOptions.minimumFreeDeliveryOrderAmount' },
              {
                path: 'localDeliveryOptions.radiusConfig.address.geocode.latitude',
              },
              {
                path: 'localDeliveryOptions.radiusConfig.address.geocode.longitude',
              },
              {
                path: 'localDeliveryOptions.customAreaConfig.geocodes.latitude',
              },
              {
                path: 'localDeliveryOptions.customAreaConfig.geocodes.longitude',
              },
              { path: 'localDeliveryOptions.radiusConfig.radius' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'localDeliveryOptions.createdDate' },
              { path: 'localDeliveryOptions.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryLocalDeliveryOptions;
}

export function updateLocalDeliveryOption(
  payload: object
): RequestOptionsFactory<any> {
  function __updateLocalDeliveryOption({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'localDeliveryOption.minimumFreeDeliveryOrderAmount' },
          { path: 'localDeliveryOption.radiusConfig.address.geocode.latitude' },
          {
            path: 'localDeliveryOption.radiusConfig.address.geocode.longitude',
          },
          { path: 'localDeliveryOption.customAreaConfig.geocodes.latitude' },
          { path: 'localDeliveryOption.customAreaConfig.geocodes.longitude' },
          { path: 'localDeliveryOption.radiusConfig.radius' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'localDeliveryOption.createdDate' },
          { path: 'localDeliveryOption.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.local_delivery_option',
      method: 'PATCH' as any,
      methodFqn:
        'com.wix.ecom.shipments.localdelivery.LocalDelivery.UpdateLocalDeliveryOption',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl({
        protoPath: '/v1/local-delivery-options/{localDeliveryOption.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'localDeliveryOption.minimumFreeDeliveryOrderAmount' },
              {
                path: 'localDeliveryOption.radiusConfig.address.geocode.latitude',
              },
              {
                path: 'localDeliveryOption.radiusConfig.address.geocode.longitude',
              },
              {
                path: 'localDeliveryOption.customAreaConfig.geocodes.latitude',
              },
              {
                path: 'localDeliveryOption.customAreaConfig.geocodes.longitude',
              },
              { path: 'localDeliveryOption.radiusConfig.radius' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'localDeliveryOption.createdDate' },
              { path: 'localDeliveryOption.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateLocalDeliveryOption;
}

export function deleteLocalDeliveryOption(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteLocalDeliveryOption({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.local_delivery_option',
      method: 'DELETE' as any,
      methodFqn:
        'com.wix.ecom.shipments.localdelivery.LocalDelivery.DeleteLocalDeliveryOption',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl({
        protoPath: '/v1/local-delivery-options/{localDeliveryOptionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteLocalDeliveryOption;
}

export function bulkCreateLocalDeliveryOption(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateLocalDeliveryOption({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'localDeliveryOptions.minimumFreeDeliveryOrderAmount' },
          {
            path: 'localDeliveryOptions.radiusConfig.address.geocode.latitude',
          },
          {
            path: 'localDeliveryOptions.radiusConfig.address.geocode.longitude',
          },
          { path: 'localDeliveryOptions.customAreaConfig.geocodes.latitude' },
          { path: 'localDeliveryOptions.customAreaConfig.geocodes.longitude' },
          { path: 'localDeliveryOptions.radiusConfig.radius' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'localDeliveryOptions.createdDate' },
          { path: 'localDeliveryOptions.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.local_delivery_option',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.shipments.localdelivery.LocalDelivery.BulkCreateLocalDeliveryOption',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl({
        protoPath: '/v1/bulk/local-delivery-options/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'localDeliveryOptions.minimumFreeDeliveryOrderAmount' },
              {
                path: 'localDeliveryOptions.radiusConfig.address.geocode.latitude',
              },
              {
                path: 'localDeliveryOptions.radiusConfig.address.geocode.longitude',
              },
              {
                path: 'localDeliveryOptions.customAreaConfig.geocodes.latitude',
              },
              {
                path: 'localDeliveryOptions.customAreaConfig.geocodes.longitude',
              },
              { path: 'localDeliveryOptions.radiusConfig.radius' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'localDeliveryOptions.createdDate' },
              { path: 'localDeliveryOptions.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateLocalDeliveryOption;
}

export function bulkUpdateLocalDeliveryOption(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateLocalDeliveryOption({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'localDeliveryOptions.minimumFreeDeliveryOrderAmount' },
          {
            path: 'localDeliveryOptions.radiusConfig.address.geocode.latitude',
          },
          {
            path: 'localDeliveryOptions.radiusConfig.address.geocode.longitude',
          },
          { path: 'localDeliveryOptions.customAreaConfig.geocodes.latitude' },
          { path: 'localDeliveryOptions.customAreaConfig.geocodes.longitude' },
          { path: 'localDeliveryOptions.radiusConfig.radius' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'localDeliveryOptions.createdDate' },
          { path: 'localDeliveryOptions.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.local_delivery_option',
      method: 'PATCH' as any,
      methodFqn:
        'com.wix.ecom.shipments.localdelivery.LocalDelivery.BulkUpdateLocalDeliveryOption',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl({
        protoPath: '/v1/bulk/local-delivery-options/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'localDeliveryOptions.minimumFreeDeliveryOrderAmount' },
              {
                path: 'localDeliveryOptions.radiusConfig.address.geocode.latitude',
              },
              {
                path: 'localDeliveryOptions.radiusConfig.address.geocode.longitude',
              },
              {
                path: 'localDeliveryOptions.customAreaConfig.geocodes.latitude',
              },
              {
                path: 'localDeliveryOptions.customAreaConfig.geocodes.longitude',
              },
              { path: 'localDeliveryOptions.radiusConfig.radius' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'localDeliveryOptions.createdDate' },
              { path: 'localDeliveryOptions.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateLocalDeliveryOption;
}

export function bulkDeleteLocalDeliveryOption(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteLocalDeliveryOption({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.local_delivery_option',
      method: 'DELETE' as any,
      methodFqn:
        'com.wix.ecom.shipments.localdelivery.LocalDelivery.BulkDeleteLocalDeliveryOption',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl({
        protoPath: '/v1/bulk/local-delivery-options/delete',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __bulkDeleteLocalDeliveryOption;
}

export function addDeliveryRegion(payload: object): RequestOptionsFactory<any> {
  function __addDeliveryRegion({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.local_delivery_option',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.shipments.localdelivery.LocalDelivery.AddDeliveryRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl({
        protoPath: '/v1/local-delivery-options/add-delivery-region',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'localDeliveryOption.minimumFreeDeliveryOrderAmount' },
              {
                path: 'localDeliveryOption.radiusConfig.address.geocode.latitude',
              },
              {
                path: 'localDeliveryOption.radiusConfig.address.geocode.longitude',
              },
              {
                path: 'localDeliveryOption.customAreaConfig.geocodes.latitude',
              },
              {
                path: 'localDeliveryOption.customAreaConfig.geocodes.longitude',
              },
              { path: 'localDeliveryOption.radiusConfig.radius' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'localDeliveryOption.createdDate' },
              { path: 'localDeliveryOption.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addDeliveryRegion;
}

export function removeDeliveryRegion(
  payload: object
): RequestOptionsFactory<any> {
  function __removeDeliveryRegion({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.local_delivery_option',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.shipments.localdelivery.LocalDelivery.RemoveDeliveryRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixEcomShipmentsLocaldeliveryLocalDeliveryUrl({
        protoPath: '/v1/local-delivery-options/remove-delivery-region',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'localDeliveryOption.minimumFreeDeliveryOrderAmount' },
              {
                path: 'localDeliveryOption.radiusConfig.address.geocode.latitude',
              },
              {
                path: 'localDeliveryOption.radiusConfig.address.geocode.longitude',
              },
              {
                path: 'localDeliveryOption.customAreaConfig.geocodes.latitude',
              },
              {
                path: 'localDeliveryOption.customAreaConfig.geocodes.longitude',
              },
              { path: 'localDeliveryOption.radiusConfig.radius' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'localDeliveryOption.createdDate' },
              { path: 'localDeliveryOption.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __removeDeliveryRegion;
}
